/**
 * 통합설정 › 회사정보 — 2026-08-25 신규 / 2026-08-26 시안 적용.
 *
 * **관리 콘솔(8444)의 `sections/CompanyInfoSection.tsx` 를 그대로 옮겨 온 화면이다.** 사용자
 * 지시로 콘솔의 회사정보를 통합설정에도 둔다 — 콘솔 쪽은 한 줄도 안 건드렸고(그대로 남는다),
 * 여기 것은 복사본이다. 두 화면은 **같은 서버 라우트**(`/api/v1/institution`)를 부르므로
 * 어느 쪽에서 고쳐도 결과가 같고 audit 도 한 줄기다.
 *
 *  - 기본정보: 회사코드(읽기) / 등록일(읽기) / 회사명 / 도메인 / 활성상태(읽기)
 *  - 사업자정보: 사업자등록번호(체크섬 검증) / 대표자명 / 업태 / 종목
 *  - 연락처·주소: 우편번호 / 주소 / 상세주소 / 전화 / 팩스 / 회사이메일
 *
 * chat-server PATCH 가 권한(ADMIN/SYSTEM_ADMIN)·사업자번호 검증·audit 을 처리한다.
 * 회사코드·활성·등록일은 읽기 전용(운영 절차에서만 바뀐다).
 *
 * ── 2026-08-26 — 시안 «통합설정 화면 시안» 의 회사정보 본문을 입혔다 ──────────────
 * **바뀐 것은 모양뿐이다.** 상태·검증·dirty 판정·부분 PATCH·저장 실패 문구는 한 줄도
 * 손대지 않았다(테스트가 고정하는 계약 그대로).
 *
 *   · 화면 머리 — 위치(통합설정 › 회사 › 회사정보) · 제목 · **저장 줄을 위로** 올렸다.
 *     예전엔 폼 맨 아래에 있어서, 긴 폼에서 저장하려면 끝까지 내려가야 했다.
 *   · 세로 한 줄이던 폼을 **카드 4장 + 두 칸 격자**로 나눴다 — 로고 / 기본정보 /
 *     사업자 정보 / 연락처·주소. 칸 사이 1px 틈이 곧 실선이다(칸 배경 = surface, 틈 = border).
 *   · 저장 알림 띠(`.company-notice`)는 머리의 «HH:MM 저장됨» 칩으로 바뀌었다.
 *   · 필드마다 붙던 아이콘은 뺐다(시안). 아이콘은 카드 머리에만 하나씩 남는다.
 *
 * 모양은 새 이름(`ci-*`, `CompanyInfoScreen.css`)에만 있다 — 콘솔에서 옮겨 온 `.company-*`
 * 규칙은 한 줄도 안 건드렸다(로고 그룹의 legacy 렌더 경로가 아직 그것을 쓴다).
 *
 * 시안에 있으나 **넣지 않은 것 둘** — 둘 다 뒤에 서버·외부 서비스가 있어야 도는 것이다:
 *   · «주소 찾기» (우편번호 검색) — 외부 우편번호 서비스가 필요하다.
 *   · «사업자등록증 확인» — 국세청 조회 연동이 필요하다.
 *   누르면 아무 일도 안 일어나는 단추를 두면 사용자는 고장으로 읽는다. 연동이 정해지면 그때 켠다.
 *   → 2026-09-17 «주소 찾기»는 켰다 — 서버 주소 검색 라우트(POST /address/search)가 생겼다.
 *     우편번호 칸 옆 단추(`ci-f__row`) · 창은 공용 `components/shared/AddressSearch`.
 *
 * 원본(콘솔)과 다른 곳:
 *   1. api 경로가 BFF 이름(`/admin/institution`) 대신 원본 라우트(`/institution`) — api/institution.ts.
 *   2. 등록일 표시의 하드코딩 'ko-KR' 을 getDateLocale() 로 바꿨다(8443 에는 하드코딩 로케일을
 *      막는 불변식 테스트가 있다 — lib/i18n-locale-hardcode.test.ts).
 *   3. 콘솔 셸 대신 통합설정 탭 본문에 얹히므로 바깥을 `.company-pane` 으로 한 겹 감쌌다.
 *   4. 위의 시안 적용.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
// ★ 순서 주의 — 공용(base) 을 먼저, 이 화면 것을 나중에. 뒤집으면 저장 단추가 민무늬가 된다.
import './console-shared.css';
import './CompanyInfo.css';
// 시안 층 — 이름(`ci-*`)이 겹치지 않아 순서에 기대지 않는다.
import './CompanyInfoScreen.css';
import { Building2, FileText, MapPin, Check, Save, AlertTriangle } from '../../icons';
// 채택 3단계 — 손으로 짠 칸·단추를 정본 공통 컴포넌트로 (디자인 가이드 B1·B2·C1).
import { Button, FormField, Input } from '../../components/shared';
import { getInstitution, updateInstitution, type Institution, type InstitutionPatch } from '../../api/institution';
// 로고 그룹 — 등록 즉시 반영이라 아래 폼의 dirty/저장 흐름과 분리된 별도 컴포넌트.
import { CompanyLogoGroup } from './CompanyLogoGroup';
// 8443 은 화면 글자를 언어 설정에 맞춘다 — 원본의 하드코딩 'ko-KR' 을 여기서만 바꿨다.
import { getDateLocale, formatTime } from '../../lib/base-i18n';
import { useAddressSearchDialog } from '../../components/shared/AddressSearch';
// 날씨 위치 (2026-09-21) — 주소에서 셈한 «파생값»이라 읽기만 한다. 고칠 것은 주소뿐이다.
import { getWeatherGridSettings, type WeatherLocation } from '../../api/weather';
import { weatherLocationCopy } from '../../lib/weather-location';
import { Badge } from '../../components/shared';
import type { JusoAddress } from '../../api/address';
// 손짠 머리 → 정본 PageHeader(2026-10-17 · web 레인 W7c — 쪽 머리 한 꼴 F4)
import { PageHeader } from '../../components/shared';

/**
 * 등록일 — **날짜만** 보인다 (2026-08-26).
 * 종전에는 시각까지 붙어 «2016. 3. 2. 오후 6:00» 이었다. 회사가 등록된 **분**은 아무도 쓰지 않는
 * 값이라 칸만 차지했고, 읽기 전용 상자 안에서 「오후 6:00」 자리가 군더더기로 읽혔다(시안도 날짜만).
 * 로케일은 계속 `getDateLocale()` 을 탄다 — 8443 에는 하드코딩 로케일을 막는 불변식 테스트가 있다.
 */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(getDateLocale(), { dateStyle: 'medium' });
}

/**
 * 저장 직후 머리에 뜨는 «HH:MM 저장됨» 칩의 시각.
 *
 * ⚠ 2026-10-06 — 같은 Intl 호출을 ★ 여기서 «다시» 쓰고 있었다 ⇒ ⭐ `formatTime`(SSOT)으로 바꿨다.
 *   ⛔ 두 벌로 두면 ★ 한국어 자정 표기(「오전 12:00」 → 「오전 0:00」) 같은 고침이 ⭐ «한쪽만» 간다.
 */
function fmtClock(d: Date): string {
  return formatTime(d);
}

const digits = (s: string): string => s.replace(/\D/g, '');

/** 한국 사업자등록번호 체크섬(10자리). 빈 값은 허용(true). 서버와 동일 알고리즘. */
function brnValid(raw: string): boolean {
  const d = digits(raw);
  if (d.length === 0) return true;
  if (!/^\d{10}$/.test(d)) return false;
  const n = d.split('').map(Number);
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += n[i]! * w[i]!;
  sum += Math.floor((n[8]! * 5) / 10);
  return ((10 - (sum % 10)) % 10) === n[9]!;
}

// 편집 가능한 모든 필드 키(문자열 상태). code/isActive/createdAt 은 읽기전용이라 제외.
type EditKey =
  | 'name' | 'domain' | 'businessRegNo' | 'representativeName' | 'businessType' | 'businessItem'
  | 'postalCode' | 'address' | 'addressDetail' | 'phone' | 'fax' | 'companyEmail';

type FormState = Record<EditKey, string>;

const EDIT_KEYS: EditKey[] = [
  'name', 'domain', 'businessRegNo', 'representativeName', 'businessType', 'businessItem',
  'postalCode', 'address', 'addressDetail', 'phone', 'fax', 'companyEmail',
];

function formFromInst(i: Institution): FormState {
  return {
    name: i.name,
    domain: i.domain ?? '',
    businessRegNo: i.businessRegNo ?? '',
    representativeName: i.representativeName ?? '',
    businessType: i.businessType ?? '',
    businessItem: i.businessItem ?? '',
    postalCode: i.postalCode ?? '',
    address: i.address ?? '',
    addressDetail: i.addressDetail ?? '',
    phone: i.phone ?? '',
    fax: i.fax ?? '',
    companyEmail: i.companyEmail ?? '',
  };
}

/**
 * 편집 칸 하나 — 라벨 / 입력 / 힌트·에러 (시안의 `.f`).
 * 힌트와 에러는 **둘 중 하나만** 보인다(에러가 이긴다) — 예전 `TextRow` 와 같은 규칙.
 */
function TextField(props: {
  id: string; label: string; value: string;
  onChange: (v: string) => void; max: number; placeholder?: string;
  hint?: string; err?: string | null; type?: string;
  required?: boolean; span?: boolean;
  /** 입력 오른쪽에 붙일 것(2026-09-17 · 우편번호 옆 「주소 찾기」). 주면 `ci-f__row` 로 한 줄에 둔다. */
  after?: ReactNode;
}) {
  const { id, label, value, onChange, max, placeholder, hint, err, type, required, span, after } = props;
  return (
    <div className={`ci-f${span ? ' ci-f--span' : ''}`}>
      <FormField label={label} htmlFor={id} required={required} hint={hint} error={err ?? undefined}>
        {after == null ? (
          <Input
            type={type ?? 'text'}
            value={value}
            maxLength={max}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            fullWidth
          />
        ) : (
          <div className="ci-f__row">
            <Input
              type={type ?? 'text'}
              value={value}
              maxLength={max}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              fullWidth
            />
            {after}
          </div>
        )}
      </FormField>
    </div>
  );
}

/**
 * 날씨 위치 — **읽기만 하는 칸** (2026-09-21 · 시안 v2).
 *
 * 주소에서 셈한 값이라 고칠 수 없다. 확정 단추도, 되돌리기도 없다 — 고칠 것은 위 「주소」 칸이다.
 */
function WeatherLocationField({ loc }: { loc: WeatherLocation }) {
  const c = weatherLocationCopy(loc, 'company');
  const variant = c.tone === 'ok' ? 'success' : c.tone === 'warn' ? 'warning' : 'default';
  return (
    <div className="ci-f ci-f--span">
      <FormField
        label={<span className="ci-wx__label">날씨 위치<Badge variant={variant} size="sm">{c.badge}</Badge></span>}
        hint={c.hint}
      >
        <p className="ci-wx">{c.line ?? '—'}</p>
      </FormField>
    </div>
  );
}

export function CompanyInfoSection() {
  const [inst, setInst] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 저장 성공 시각 — 머리의 «HH:MM 저장됨» 칩. (예전엔 본문 위 알림 띠였다.)
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  // null = 아직 못 읽음(서버가 옛 판이면 영영 null — 그때는 칸을 그리지 않는다).
  const [weatherLoc, setWeatherLoc] = useState<WeatherLocation | null>(null);

  const set = useCallback((k: EditKey, v: string) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  }, []);

  // 주소 찾기 (2026-09-17) — 우편번호 · 주소만 채운다(상세주소는 그대로) · 고른 뒤 상세주소로 포커스.
  const applyAddress = useCallback((a: JusoAddress) => {
    setForm((f) => (f ? { ...f, postalCode: a.zipNo, address: a.roadAddr } : f));
  }, []);
  const focusAddressDetail = useCallback(() => document.getElementById('company-addr2'), []);
  const addressSearch = useAddressSearchDialog({ onApply: applyAddress, focusAfterPick: focusAddressDetail });

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const i = await getInstitution();
      setInst(i);
      setForm(formFromInst(i));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.response?.data?.error ?? e?.message ?? '회사정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // 주소를 저장하면 위치가 따라간다 — 저장 뒤 다시 읽는다(inst 가 바뀔 때).
  useEffect(() => {
    let alive = true;
    getWeatherGridSettings()
      .then((r) => { if (alive) setWeatherLoc(r.company.location); })
      .catch(() => { /* 옛 서버 · 권한 없음 — 칸을 그리지 않는다 */ });
    return () => { alive = false; };
  }, [inst]);

  // 검증
  const nameVal = (form?.name ?? '').trim();
  const nameInvalid = nameVal.length === 0 || nameVal.length > 100;
  const domainInvalid = (form?.domain ?? '').trim().length > 100;
  const brnInvalid = !!form && !brnValid(form.businessRegNo);
  const emailVal = (form?.companyEmail ?? '').trim();
  const emailInvalid = emailVal.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);

  // dirty: businessRegNo 는 숫자만 비교, 나머지는 trim 비교.
  const dirty = useMemo(() => {
    if (!inst || !form) return false;
    for (const k of EDIT_KEYS) {
      const cur = k === 'businessRegNo' ? digits(form[k]) : form[k].trim();
      const orig = k === 'businessRegNo'
        ? digits(inst.businessRegNo ?? '')
        : (((inst[k as keyof Institution] as string | null) ?? '')).trim();
      if (cur !== orig) return true;
    }
    return false;
  }, [inst, form]);

  const canSave = dirty && !nameInvalid && !domainInvalid && !brnInvalid && !emailInvalid && !saving;

  function buildPatch(): InstitutionPatch {
    if (!inst || !form) return {};
    const patch: InstitutionPatch = {};
    for (const k of EDIT_KEYS) {
      const cur = k === 'businessRegNo' ? digits(form[k]) : form[k].trim();
      const orig = k === 'businessRegNo'
        ? digits(inst.businessRegNo ?? '')
        : (((inst[k as keyof Institution] as string | null) ?? '')).trim();
      if (cur === orig) continue;
      if (k === 'name') patch.name = form.name.trim();
      else if (k === 'businessRegNo') patch.businessRegNo = form.businessRegNo.trim() === '' ? null : form.businessRegNo.trim();
      else (patch as Record<string, unknown>)[k] = form[k].trim() === '' ? null : form[k].trim();
    }
    return patch;
  }

  async function save() {
    if (!inst || !form || !canSave) return;
    setSaving(true); setError(null); setSavedAt(null);
    try {
      const updated = await updateInstitution(buildPatch());
      setInst(updated);
      setForm(formFromInst(updated));
      setSavedAt(fmtClock(new Date()));
    } catch (e: any) {
      const code = e?.response?.status;
      const apiCode = e?.response?.data?.code;
      if (code === 403) setError('수정 권한이 없습니다. 회사 관리자(ADMIN) 권한이 필요합니다.');
      else if (apiCode === 'INVALID_BUSINESS_REG_NO') setError('사업자등록번호 형식이 올바르지 않습니다(체크섬 불일치).');
      else if (apiCode === 'BUSINESS_REG_NO_DUPLICATE') setError('이미 등록된 사업자등록번호입니다.');
      else setError(e?.response?.data?.message ?? e?.response?.data?.error ?? '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }

  function resetEdits() {
    if (!inst) return;
    setForm(formFromInst(inst));
    setError(null); setSavedAt(null);
  }

  return (
    <div className="company-pane ci">
      {/* ── 화면 머리 — 위치 · 제목 · 저장 줄 (시안 `.chead`) ───────────────── */}
      <PageHeader
        breadcrumb={['통합설정', '회사', '회사정보']}
        scope="admin"
        title="회사정보"
        description="관리 콘솔의 같은 화면과 같은 서버를 봅니다"
        actions={(
          <div className="ci-acts">
            {savedAt && !dirty && (
              <span className="ci-saved" role="status"><Check size={13} aria-hidden /> {savedAt} 저장됨</span>
            )}
            <Button variant="secondary" disabled={!dirty || saving} onClick={resetEdits}>
              되돌리기
            </Button>
            <Button
              variant="primary"
              disabled={!canSave}
              loading={saving}
              iconLeft={saving ? undefined : <Save size={13} aria-hidden />}
              onClick={() => void save()}
            >
              {saving ? '저장 중…' : '저장'}
            </Button>
          </div>
        )}
      />

      {error && <div className="ci-alert" role="alert"><AlertTriangle size={14} aria-hidden /> {error}</div>}

      {loading ? (
        <p className="ci-empty">불러오는 중…</p>
      ) : !inst || !form ? (
        <p className="ci-empty">회사정보가 없습니다.</p>
      ) : (
        <div className="ci-body">
          {/* ── 로고 (2026-08-12) — 사용자 사이트 좌측 레일 브랜드에 즉시 반영.
                 저장/되돌리기와 무관하게 그 자리에서 끝난다. ── */}
          <section className="ci-card">
            <CompanyLogoGroup
              variant="row"
              onChanged={(next) => setInst((cur) => (cur ? { ...cur, logoUrl: next.logoUrl } : next))}
            />
          </section>

          {/* ── 직인 (2026-08-28 · 판 8) — 로고 **옆**이 아니라 아래 한 줄이다. 로고와 규칙은
                 같지만 뜻이 다르다: 로고는 화면의 브랜드, 직인은 **문서에 찍히는 것**이다.
                 증명서에 자리는 원래 있었고 넣을 그림이 없었을 뿐이다. ── */}
          <section className="ci-card">
            <CompanyLogoGroup
              kind="seal"
              variant="row"
              onChanged={(next) => setInst((cur) => (cur ? { ...cur, sealUrl: next.sealUrl } : next))}
            />
          </section>

          {/* ── 기본정보 ── */}
          <section className="ci-card">
            <div className="ci-card__h">
              <Building2 size={17} aria-hidden />
              <span className="ci-card__t">기본정보</span>
              <span className="ci-card__sub">회사코드와 활성 상태는 운영 절차에서만 바뀝니다</span>
            </div>
            <div className="ci-fields">
              <div className="ci-f">
                {/* 라벨이 아니라 이름표다 — 고칠 수 있는 칸으로 오해하면 안 된다. */}
                <span className="ci-f__label">회사코드</span>
                <div className="ci-ctl ci-ctl--ro">
                  <code className="ci-code">{inst.code}</code>
                  <span className={`ci-pill ${inst.isActive ? 'is-on' : 'is-off'}`}>
                    <i className="ci-pill__d" aria-hidden />{inst.isActive ? '활성' : '비활성'}
                  </span>
                </div>
                <p className="ci-f__hint">읽기 전용</p>
              </div>

              <div className="ci-f">
                <span className="ci-f__label">등록일</span>
                <div className="ci-ctl ci-ctl--ro">{fmtDate(inst.createdAt)}</div>
                <p className="ci-f__hint">읽기 전용</p>
              </div>

              <TextField
                id="company-name" label="회사명" required
                value={form.name} onChange={(v) => set('name', v)} max={100} placeholder="회사명"
                hint="최대 100자"
                err={nameInvalid ? '회사명은 1–100자여야 합니다.' : null}
              />
              <TextField
                id="company-domain" label="도메인"
                value={form.domain} onChange={(v) => set('domain', v)} max={100} placeholder="예: example.com (선택)"
                hint="SSO·도메인 검증에 사용됩니다. 비우면 도메인 없음으로 저장됩니다."
                err={domainInvalid ? '도메인은 100자 이하여야 합니다.' : null}
              />
            </div>
          </section>

          {/* ── 사업자 정보 ── */}
          <section className="ci-card">
            <div className="ci-card__h">
              <FileText size={17} aria-hidden />
              <span className="ci-card__t">사업자 정보</span>
              <span className="ci-card__sub">모두 선택 입력</span>
            </div>
            <div className="ci-fields">
              <TextField
                id="company-brn" label="사업자등록번호"
                value={form.businessRegNo} onChange={(v) => set('businessRegNo', v)} max={14}
                placeholder="예: 123-45-67890 (선택)"
                hint="하이픈 없이 입력해도 됩니다. 비우면 값 없음으로 저장됩니다."
                err={brnInvalid ? '사업자등록번호가 올바르지 않습니다(체크섬 불일치).' : null}
              />
              <TextField
                id="company-rep" label="대표자명"
                value={form.representativeName} onChange={(v) => set('representativeName', v)} max={50}
                placeholder="대표자명 (선택)"
              />
              <TextField
                id="company-biztype" label="업태"
                value={form.businessType} onChange={(v) => set('businessType', v)} max={100}
                placeholder="예: 서비스업 (선택)"
              />
              <TextField
                id="company-bizitem" label="종목"
                value={form.businessItem} onChange={(v) => set('businessItem', v)} max={100}
                placeholder="예: 소프트웨어 개발 (선택)"
              />
            </div>
          </section>

          {/* ── 연락처 · 주소 ── */}
          <section className="ci-card">
            <div className="ci-card__h">
              <MapPin size={17} aria-hidden />
              <span className="ci-card__t">연락처 · 주소</span>
              <span className="ci-card__sub">모두 선택 입력</span>
            </div>
            <div className="ci-fields">
              <TextField
                id="company-postal" label="우편번호"
                value={form.postalCode} onChange={(v) => set('postalCode', v)} max={10}
                placeholder="예: 06236 (선택)"
                after={
                  <Button variant="secondary" size="md" iconLeft={<MapPin size={14} aria-hidden />} onClick={addressSearch.open}>
                    주소 찾기
                  </Button>
                }
              />
              <TextField
                id="company-addr" label="주소"
                value={form.address} onChange={(v) => set('address', v)} max={200}
                placeholder="기본주소 (선택)"
              />
              <TextField
                id="company-addr2" label="상세주소" span
                value={form.addressDetail} onChange={(v) => set('addressDetail', v)} max={200}
                placeholder="상세주소 (선택)"
              />
              {weatherLoc && <WeatherLocationField loc={weatherLoc} />}
              <TextField
                id="company-phone" label="전화번호"
                value={form.phone} onChange={(v) => set('phone', v)} max={30}
                placeholder="예: 02-1234-5678 (선택)"
              />
              <TextField
                id="company-fax" label="팩스"
                value={form.fax} onChange={(v) => set('fax', v)} max={30}
                placeholder="예: 02-1234-5679 (선택)"
              />
              <TextField
                id="company-email" label="회사 이메일" span type="email"
                value={form.companyEmail} onChange={(v) => set('companyEmail', v)} max={255}
                placeholder="예: contact@example.com (선택)"
                err={emailInvalid ? '이메일 형식이 올바르지 않습니다.' : null}
              />
            </div>
          </section>
        </div>
      )}
      {addressSearch.dialog}
    </div>
  );
}
